import { isSelfHosted } from '../config/edition'
import { hasConsent } from './consent'
import { useAuthStore } from '../store/authStore'

const VID_KEY = '_oaas_vid'
const SID_KEY = '_oaas_sid'
const UTM_KEY = '_oaas_utm'

function getId(storage: Storage, key: string): string {
  let id = storage.getItem(key)
  if (!id) {
    id = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
    storage.setItem(key, id)
  }
  return id
}

interface UtmParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
}

// Read UTM from the current URL only - no storage touched. Safe in both
// consent states.
function readUtmFromUrl(): UtmParams {
  const p = new URLSearchParams(location.search)
  return {
    utm_source:   p.get('utm_source')   || undefined,
    utm_medium:   p.get('utm_medium')   || undefined,
    utm_campaign: p.get('utm_campaign') || undefined,
    utm_content:  p.get('utm_content')  || undefined,
  }
}

// Consented path: if UTM is present, persist to sessionStorage so downstream
// pages (e.g. /register navigated to from /pricing) carry the same
// attribution. Falls back to the session-stored value when the current URL
// has no UTM params.
function getUtm(): UtmParams {
  const fromUrl = readUtmFromUrl()
  if (fromUrl.utm_source) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify(fromUrl))
    return fromUrl
  }
  try {
    const stored = sessionStorage.getItem(UTM_KEY)
    if (stored) return JSON.parse(stored) as UtmParams
  } catch { /* ignore */ }
  return fromUrl
}

export function track(eventType: string): void {
  // Marketing analytics is SaaS-only; the /track endpoint doesn't exist in
  // the self-hosted edition, so skip it entirely.
  if (isSelfHosted) return
  try {
    // Two modes, decided by the "analytics" cookie consent:
    //
    // Consented: persistent visitor id in localStorage + session id, full
    // cross-visit attribution. Same gate as PostHog.
    //
    // Not consented ("necessary only" or no choice yet): the visit is still
    // counted, ANONYMOUSLY. Nothing is read from or written to the device -
    // no cookie, no localStorage, no sessionStorage - which is exactly why
    // this needs no consent (the Plausible/Fathom model). The server derives
    // a DAILY-ROTATING id from ip+ua, so uniques count within a day but
    // cross-day matching is impossible by design. UTM comes from the current
    // URL only and is not persisted across pages.
    const payload = hasConsent('analytics')
      ? {
          event_type: eventType,
          visitor_id: getId(localStorage, VID_KEY),
          session_id: getId(sessionStorage, SID_KEY),
          path: location.pathname,
          referrer: document.referrer || undefined,
          ...getUtm(),
        }
      : {
          event_type: eventType,
          anonymous: true,
          path: location.pathname,
          referrer: document.referrer || undefined,
          ...readUtmFromUrl(),
        }
    const body = JSON.stringify(payload)
    // Logged-in usage measurement (legitimate interest, opt-out on the
    // Profile page): the session token rides along so the server can tie
    // the pageview to the account. sendBeacon cannot carry headers, so the
    // authenticated path uses keepalive fetch, which survives navigation
    // the same way. The server drops the attribution for opted-out users.
    const token = useAuthStore.getState().accessToken
    if (token) {
      void fetch('/api/v1/track', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body,
      }).catch(() => {})
    } else {
      // sendBeacon is fire-and-forget; no await needed
      navigator.sendBeacon('/api/v1/track', body)
    }
  } catch {
    // Never let tracking errors surface to the user
  }
}
